// #2121-spec-kits
import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Card, CardBody, Button, Input, Spinner,
  Dropdown, DropdownToggle, DropdownMenu, DropdownItem,
  Modal, ModalHeader, ModalBody, ModalFooter
} from 'reactstrap';
import { translate } from 'react-translate';
import TablePatientKits from './TablePatientKits';
import TableKits from './TableKits';
import Paginator from '../Common/Paginator';
import {
  actionGetPatientKits,
  actionGetKitsList,
  actionGetKitsStateCount,
  actionSetKitActiveTab,
  actionClearKit
} from 'store/actions/kitsActions';
import { useHistory } from 'react-router-dom';


const TABS = [
  { key: 'free',                       label: 'free' },
  { key: 'k_st_3',  label: 'k_st_3' },
  { key: 'k_st_4',   label: 'k_st_4' },
  { key: 'k_st_5',         label: 'k_st_5' },
  { key: 'k_st_9',        label: 'k_st_9' },
  { key: 'k_st_6',              label: 'k_st_6' },
  { key: 'k_st_8',                label: 'k_st_8' },
  { key: 'k_st_7',               label: 'k_st_7' },
  { key: 'all',                        label: 'all' },
];

const TAB_TONE = {
  free:                       'success',
  k_st_3:  'info',
  k_st_4:   'primary',
  'k_st_5':       'warning',
  k_st_9:        'warning',
  k_st_6:              'danger',
  k_st_8:                'danger',
  k_st_7:               'secondary',
  all:                        'dark',
};

const isFlatList = (tab) =>
  tab === 'free' || tab === 'k_st_3' || tab === 'all';

const KitsList = (props) => {
  const { t } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  

  const patientKits = useSelector((s) => s.kitsReducer.patientKits);
  const kitList     = useSelector((s) => s.kitsReducer.kits);
  const kitCount    = useSelector((s) => s.kitsReducer.kitsCountByState);
  const activeTab   = useSelector((s) => s.kitsReducer.activeTab);

  const [_page, setPage]            = useState(0);
  const [_limit]                    = useState(20);
  const [totalPage, setTotalPage]   = useState(0);
  const [filter, setFilter]         = useState(null);
  const [loadingLocal, setLoading]  = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [maxVisible, setMaxVisible] = useState(TABS.length);
  const [moreOpen, setMoreOpen]     = useState(false);
  const [modalOpen, setModalOpen]   = useState(false);
  const [kitId, setKitId]   = useState(false);

  const tabsContainerRef = useRef(null);
  const phantomRefs      = useRef({});
  const morePhantomRef   = useRef(null);

  const fireFetch = (page, currentFilter) => {
    setLoading(true);
    if (activeTab === 'all') {
      dispatch(actionGetKitsList(page, _limit, null, currentFilter));
    } else if (activeTab === 'free') {
      dispatch(actionGetKitsList(page, _limit, 'k_st_2,k_st_1', currentFilter));
    } else if (activeTab === 'k_st_3') {
      dispatch(actionGetKitsList(page, _limit, 'k_st_3', currentFilter));
    } else {
      dispatch(actionGetPatientKits(page, _limit, activeTab, currentFilter));
    }
  };

  const clearKitAction = (kitId) => {
    modalToggle();
    setKitId(kitId);
  }

  const clearKitLogic = () => {
    modalToggle();
    dispatch(actionClearKit(kitId, _page, _limit, activeTab === 'all' ? null : activeTab, filter));
  }

  const modalToggle = () => {
    setModalOpen(!modalOpen);
  };

  const toggle = (tab) => {
    if (activeTab === tab) return;
    dispatch(actionSetKitActiveTab(tab));
    setPage(0);
    setSearchTerm('');
    setFilter(null);
    setMoreOpen(false);
  };

  useEffect(() => {
    dispatch(actionGetKitsStateCount());
    fireFetch(0, null);
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (isFlatList(activeTab) && kitList && kitList.data) {
      setLoading(false);
      setPage(kitList.page);
      setTotalPage(kitList.lastPage);
    } else if (patientKits && patientKits.data) {
      setLoading(false);
      setPage(patientKits.page);
      setTotalPage(patientKits.lastPage);
    }
  }, [kitList, patientKits, activeTab]);

  useEffect(() => {
    const t = setTimeout(() => {
      const value = searchTerm && searchTerm.length > 2 ? searchTerm.trim() : null;
      if (value !== filter) {
        setFilter(value);
        fireFetch(0, value);
        setPage(0);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // ---------- overflow tabs (priority+ pattern) ----------
  // Estrategia: dejamos que el browser renderice y medimos el container real.
  // Si scrollWidth > clientWidth, hay overflow -> decrementamos maxVisible.
  // Cuando el container crece (resize), reseteamos a TABS.length y dejamos que
  // este efecto vuelva a decrementar hasta el valor estable. Es un loop de
  // pocas iteraciones que NO depende de phantoms ni de font loading.

  // Despues de cada render, si overflowea, decrementamos.
  useLayoutEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;
    if (container.clientWidth === 0) return;
    if (container.scrollWidth > container.clientWidth + 2) {
      setMaxVisible((prev) => Math.max(1, prev - 1));
    }
  });

  // En cambios de viewport / kitCount / activeTab reseteamos maxVisible.
  // El otro effect (de arriba) bajara el valor si aun overflowea.
  useEffect(() => {
    const reset = () => setMaxVisible(TABS.length);
    reset();

    let observer;
    if (typeof ResizeObserver !== 'undefined' && tabsContainerRef.current) {
      observer = new ResizeObserver(reset);
      observer.observe(tabsContainerRef.current);
    }
    window.addEventListener('resize', reset);

    // Re-evaluar cuando cargan las fuentes custom (Montserrat cambia anchos
    // respecto a la system font del primer paint).
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(reset);
    }

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', reset);
    };
  }, [kitCount, activeTab]);

  const onPageChange = (page) => fireFetch(page, filter);

  const createKit = (e) => {
    e.preventDefault();
    history.push('/admin/kits/form/');
  };

  const totalCount = (key) => {
    if (key === 'free') return (kitCount.k_st_2 || 0) + (kitCount.k_st_1 || 0);
    return kitCount[key] || 0;
  };

  const dataForTable = isFlatList(activeTab)
    ? (kitList && kitList.data) || []
    : (patientKits && patientKits.data) || [];
  const totalEntries = isFlatList(activeTab)
    ? (kitList && kitList.total) || 0
    : (patientKits && patientKits.total) || 0;

  const overflowingTabs = TABS.slice(maxVisible);
  const isActiveInOverflow = overflowingTabs.some((tt) => tt.key === activeTab);

  const renderTabButton = (tab) => {
    const count  = totalCount(tab.key);
    const tone   = TAB_TONE[tab.key] || 'secondary';
    const active = activeTab === tab.key;
    return (
      <button
        key={tab.key}
        type="button"
        role="tab"
        aria-selected={active}
        className={`kit-tab ${active ? 'active' : ''}`}
        onClick={() => toggle(tab.key)}
      >
        <span className={`kit-tab-dot tone-${tone}`} />
        <span className="kit-tab-label">{t(tab.label)}</span>
        <span className="kit-tab-count">{count}</span>
      </button>
    );
  };

  

  return (
    <div className="content kit-list-page mt-3">

      <Modal isOpen={modalOpen} toggle={modalToggle} size="sm" className="doctor-details-modal">
        <ModalHeader>
          Are you sure?
        </ModalHeader>
        <ModalBody>
          Are you sure you want to clean this kit?
        </ModalBody>
        <ModalFooter>
          <Button color="info" onClick={() => { clearKitLogic() }}>
            Yes
          </Button>
          <Button color="primary" onClick={modalToggle}>
            No
          </Button>
        </ModalFooter>
      </Modal>

      

      {/* Page header */}
      <div className="kit-list-header">
        <h2 className="kit-list-page-title">
          {t('list-subtitle') || 'Inventory, orders and patient assignments.'}
        </h2>
        <Button color="primary" onClick={createKit} className="kit-list-add">
          <i className="now-ui-icons ui-1_simple-add" /> {t('add-new-kit') || 'Add new kit'}
        </Button>
      </div>

      {/* Search prominente */}
      <div className="kit-list-search">
        <i className="now-ui-icons ui-1_zoom-bold kit-list-search-icon" />
        <Input
          type="text"
          value={searchTerm}
          placeholder={t('search-rich-placeholder') || 'Search by ID, practice, doctor or patient...'}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            type="button"
            className="kit-list-search-clear"
            onClick={() => setSearchTerm('')}
            aria-label="Clear"
          >
            <i className="now-ui-icons ui-1_simple-remove" />
          </button>
        )}
        {searchTerm && searchTerm.length > 0 && searchTerm.length <= 2 && (
          <small className="kit-list-search-hint">{t('search-hint') || 'Type at least 3 characters'}</small>
        )}
      </div>

      {/* Phantom — fuera de pantalla, solo para medir widths */}
      <div className="kit-tabs-phantom" aria-hidden="true">
        {TABS.map((tab) => {
          const count = totalCount(tab.key);
          const tone  = TAB_TONE[tab.key] || 'secondary';
          return (
            <button
              key={`p-${tab.key}`}
              ref={(el) => { phantomRefs.current[tab.key] = el; }}
              className="kit-tab"
              type="button"
              tabIndex={-1}
            >
              <span className={`kit-tab-dot tone-${tone}`} />
              <span className="kit-tab-label">{t(tab.label)}</span>
              <span className="kit-tab-count">{count}</span>
            </button>
          );
        })}
        {/* Phantom del More: medimos su ancho real para reservarle espacio */}
        <button
          ref={morePhantomRef}
          className="kit-tab kit-tab-more-toggle"
          type="button"
          tabIndex={-1}
        >
          {t('more') || 'More'}
          <span className="kit-tab-count">{TABS.length}</span>
          <i className="now-ui-icons arrows-1_minimal-down kit-tab-more-caret" />
        </button>
      </div>

      {/* Tabs visibles + overflow dropdown */}
      <div className="kit-tabs" ref={tabsContainerRef} role="tablist">
        {TABS.slice(0, maxVisible).map(renderTabButton)}

        {overflowingTabs.length > 0 && (
          <Dropdown
            isOpen={moreOpen}
            toggle={() => setMoreOpen(!moreOpen)}
            className="kit-tab-more"
          >
            <DropdownToggle
              tag="button"
              type="button"
              className={`kit-tab kit-tab-more-toggle ${isActiveInOverflow ? 'active' : ''}`}
            >
              {t('more') || 'More'}
              <span className="kit-tab-count">{overflowingTabs.length}</span>
              <i className="now-ui-icons arrows-1_minimal-down kit-tab-more-caret" />
            </DropdownToggle>
            <DropdownMenu right className="kit-tab-more-menu">
              {overflowingTabs.map((tab) => {
                const count  = totalCount(tab.key);
                const tone   = TAB_TONE[tab.key] || 'secondary';
                const active = activeTab === tab.key;
                return (
                  <DropdownItem
                    key={tab.key}
                    active={active}
                    onClick={() => toggle(tab.key)}
                    className="kit-tab-more-item"
                  >
                    <span className={`kit-tab-dot tone-${tone}`} />
                    <span className="kit-tab-label">{t(tab.label)}</span>
                    <span className="kit-tab-count">{count}</span>
                  </DropdownItem>
                );
              })}
            </DropdownMenu>
          </Dropdown>
        )}
      </div>

      {/* Tabla */}
      <Card className="kit-list-card">
        <CardBody className="kit-list-card-body">
          {loadingLocal ? (
            <div className="kit-list-loading">
              <Spinner color="primary" />
            </div>
          ) : dataForTable.length === 0 ? (
            <div className="kit-list-empty">
              <i className="now-ui-icons shopping_box" />
              <h5>{t('empty-title') || 'No kits to show'}</h5>
              <p>{filter ? (t('empty-search') || 'No matches for the current search.')
                         : (t('empty-tab')    || 'There are no kits in this state yet.')}</p>
            </div>
          ) : isFlatList(activeTab) ? (
            <TableKits
              tabInfo={activeTab}
              data={dataForTable}
              clearKitAction={clearKitAction}
              loading={false}
              page={_page}
              callback={onPageChange}
            />
          ) : (
            <TablePatientKits
              data={dataForTable}
              clearKitAction={clearKitAction}
              loading={false}
              page={_page}
              callback={onPageChange}
            />
          )}

          {dataForTable.length > 0 && (
            <>
              <div className="kit-list-total">
                {t('showing') || 'Showing'} {dataForTable.length} {t('of') || 'of'} {totalEntries}
              </div>
              <div className="d-flex justify-content-center align-items-center mt-3">
                <Paginator
                  totalPages={totalPage}
                  currentPage={_page}
                  pageSize={_limit}
                  filter={filter}
                  callback={onPageChange}
                />
              </div>
            </>
          )}
        </CardBody>
      </Card>

    </div>
  );
};

export default translate('kits')(KitsList);
